import { Timestamp } from 'firebase/firestore';

export type PlanId = 'free' | 'trial' | 'pro' | 'premium';
export type PlanStatus = 'free' | 'inactive' | 'active' | 'trialing' | 'canceled' | 'past_due';
export type PlanPeriodEnd = Timestamp | number | null;

export type PlanRecord = {
  id: PlanId;
  tier: PlanId;
  status: PlanStatus;
  currentPeriodEnd: PlanPeriodEnd;
};

export function normalizePlanId(value: unknown): PlanId {
  const normalized = String(value || '').trim().toLowerCase();

  if (normalized === 'trial') return 'trial';
  if (normalized === 'basic') return 'pro'; // legado: basic → pro
  if (normalized === 'pro') return 'pro';
  if (normalized === 'premium') return 'premium';
  return 'free';
}

export function normalizePlanStatus(value: unknown): PlanStatus {
  const normalized = String(value || '').trim().toLowerCase();

  if (normalized === 'free') return 'free';
  if (normalized === 'active') return 'active';
  if (normalized === 'trialing' || normalized === 'trial_active' || normalized === 'trial') return 'trialing';
  if (normalized === 'canceled') return 'canceled';
  if (normalized === 'past_due') return 'past_due';
  return 'inactive';
}

export function normalizePlanPeriodEnd(value: unknown): PlanPeriodEnd {
  if (value == null || value === '') {
    return null;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (value instanceof Timestamp) {
    return value;
  }

  if (typeof value === 'object' && value !== null && 'toMillis' in value) {
    const candidate = value as { toMillis?: () => number };
    if (typeof candidate.toMillis === 'function') {
      return Timestamp.fromMillis(candidate.toMillis());
    }
  }

  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return Timestamp.fromMillis(parsed);
    }
  }

  return null;
}

export function buildPlanRecord(params: {
  id: PlanId;
  status?: PlanStatus;
  currentPeriodEnd?: PlanPeriodEnd;
}): PlanRecord {
  const id = normalizePlanId(params.id);

  return {
    id,
    tier: id,
    status: params.status || 'inactive',
    currentPeriodEnd: params.currentPeriodEnd ?? null,
  };
}

export function readPlanId(userData: Record<string, any> | null | undefined): PlanId {
  const rawPlan = userData?.plan;

  if (typeof rawPlan === 'string') {
    return normalizePlanId(rawPlan);
  }

  if (rawPlan && typeof rawPlan === 'object') {
    const planObject = rawPlan as { id?: unknown; tier?: unknown };
    const normalized = normalizePlanId(planObject.id ?? planObject.tier);
    if (normalized !== 'free' || planObject.id || planObject.tier) {
      return normalized;
    }
  }

  return normalizePlanId(userData?.planTier ?? userData?.tier ?? userData?.role);
}

export function readPlanStatus(userData: Record<string, any> | null | undefined): PlanStatus {
  const rawPlan = userData?.plan;

  if (rawPlan && typeof rawPlan === 'object') {
    const planObject = rawPlan as { status?: unknown };
    return normalizePlanStatus(planObject.status ?? userData?.subscription?.status ?? userData?.subscriptionStatus);
  }

  return normalizePlanStatus(userData?.subscription?.status ?? userData?.subscriptionStatus);
}

export function readPlanPeriodEnd(userData: Record<string, any> | null | undefined): number | null {
  const rawPlan = userData?.plan;
  const value =
    rawPlan && typeof rawPlan === 'object'
      ? (rawPlan as { currentPeriodEnd?: unknown }).currentPeriodEnd
      : userData?.currentPeriodEnd ?? userData?.trialEndsAt;

  const normalized = normalizePlanPeriodEnd(value);
  if (normalized == null) {
    return null;
  }

  return typeof normalized === 'number' ? normalized : normalized.toMillis();
}

export function buildInactivePlan(): PlanRecord {
  return buildPlanRecord({
    id: 'free',
    status: 'free',
    currentPeriodEnd: null,
  });
}

export function buildPlanPatch(plan: PlanRecord) {
  return {
    plan,
    planTier: plan.tier,
    tier: plan.tier,
    role: plan.tier,
  };
}
